import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  Fab,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { PlayArrowOutlined, RefreshOutlined, ArrowDropDownOutlined, SearchOutlined, CloseOutlined, PersonSearchOutlined, DeleteOutlined, ChatOutlined } from '@mui/icons-material'
import { formatInTimeZone } from 'date-fns-tz'
import { apiService, Scan } from '../services/api'
import ChatAgent from '../components/ChatAgent'

export default function Dashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [scanResult, setScanResult] = useState<any>(null)
  const [chatOpen, setChatOpen] = useState(false)

  const { data: scans, isLoading, error, refetch } = useQuery({
    queryKey: ['scans'],
    queryFn: () => apiService.listScans(),
  })

  const triggerScanMutation = useMutation({
    mutationFn: (daysFilter?: number) => apiService.triggerScan(daysFilter),
    onSuccess: (data) => {
      setSuccessMessage(`Scan started successfully! Scan ID: ${data.scan_id}`)
      queryClient.invalidateQueries({ queryKey: ['scans'] })
      setTimeout(() => setSuccessMessage(null), 5000)
    },
  })

  const clearScansMutation = useMutation({
    mutationFn: () => apiService.clearAllScans(),
    onSuccess: (data) => {
      setSuccessMessage(`Successfully cleared ${data.deleted_count} scans`)
      queryClient.invalidateQueries({ queryKey: ['scans'] })
      setTimeout(() => setSuccessMessage(null), 5000)
    },
  })

  const handleTriggerScan = (daysFilter?: number) => {
    triggerScanMutation.mutate(daysFilter)
    setAnchorEl(null)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleViewScan = (scanId: string) => {
    navigate(`/scans/${scanId}`)
  }

  const handleSearch = async () => {
    if (searchQuery.trim().length < 2) return

    setSearchLoading(true)
    setScanResult(null)
    try {
      const results = await apiService.searchCandidates(searchQuery)
      setSearchResults(results)
    } catch (error) {
      console.error('Search failed:', error)
    } finally {
      setSearchLoading(false)
    }
  }

  const handleScanCandidate = async (candidateId: string) => {
    setSearchLoading(true)
    try {
      const result = await apiService.scanIndividualCandidate(candidateId)
      setScanResult(result)
    } catch (error) {
      console.error('Scan failed:', error)
    } finally {
      setSearchLoading(false)
    }
  }

  const handleCloseSearch = () => {
    setSearchDialogOpen(false)
    setSearchQuery('')
    setSearchResults([])
    setScanResult(null)
  }

  const handleClearScans = () => {
    if (window.confirm('Are you sure you want to clear all scans? This action cannot be undone.')) {
      clearScansMutation.mutate()
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success'
      case 'in_progress':
        return 'warning'
      case 'failed':
        return 'error'
      default:
        return 'default'
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PersonSearchOutlined />}
            onClick={() => setSearchDialogOpen(true)}
          >
            Search Candidate
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlined />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlined />}
            onClick={handleClearScans}
            disabled={clearScansMutation.isPending || !scans || scans.length === 0}
          >
            Clear Scans
          </Button>
          <ButtonGroup variant="contained" disabled={triggerScanMutation.isPending}>
            <Button
              startIcon={<PlayArrowOutlined />}
              onClick={() => handleTriggerScan()}
            >
              {triggerScanMutation.isPending ? 'Starting...' : 'Run Scan'}
            </Button>
            <Button size="small" onClick={handleMenuOpen}>
              <ArrowDropDownOutlined />
            </Button>
          </ButtonGroup>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={() => handleTriggerScan()}>
              All Candidates
            </MenuItem>
            <MenuItem onClick={() => handleTriggerScan(30)}>
              Last Month Only
            </MenuItem>
            <MenuItem onClick={() => handleTriggerScan(14)}>
              Last 2 Weeks Only
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {triggerScanMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to trigger scan: {(triggerScanMutation.error as Error).message}
        </Alert>
      )}

      {clearScansMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to clear scans: {(clearScansMutation.error as Error).message}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Scans
          </Typography>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error">
              Failed to load scans: {(error as Error).message}
            </Alert>
          )}

          {scans && scans.length === 0 && (
            <Typography color="text.secondary">No scans found. Run your first scan!</Typography>
          )}

          {scans && scans.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Scan ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Trigger</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Suspicious</TableCell>
                    <TableCell>Flagged Rate</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scans.map((scan: Scan) => (
                    <TableRow key={scan.scan_id} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {scan.scan_id.substring(0, 8)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={scan.status}
                          color={getStatusColor(scan.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={scan.trigger} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {formatInTimeZone(new Date(scan.started_at), 'America/New_York', 'MMM dd, yyyy HH:mm zzz')}
                      </TableCell>
                      <TableCell>{scan.summary?.total_candidates ?? '-'}</TableCell>
                      <TableCell>{scan.summary?.suspicious_candidates ?? '-'}</TableCell>
                      <TableCell>
                        {scan.summary
                          ? `${(scan.summary.flagged_rate * 100).toFixed(1)}%`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => handleViewScan(scan.scan_id)}>
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={searchDialogOpen} onClose={handleCloseSearch} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Search Candidate in JazzHR</Typography>
            <IconButton onClick={handleCloseSearch}>
              <CloseOutlined />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 1 }}>
            <TextField
              fullWidth
              placeholder="Enter candidate name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: <SearchOutlined sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={searchLoading || searchQuery.trim().length < 2}
            >
              Search
            </Button>
          </Box>

          {searchLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {scanResult && (
            <Alert severity={scanResult.is_suspicious ? 'error' : 'success'} sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {scanResult.candidate_name} - {scanResult.is_suspicious ? 'Suspicious' : 'Clean'}
              </Typography>
              <Typography><strong>Email:</strong> {scanResult.candidate_email || 'N/A'}</Typography>
              <Typography><strong>Confidence:</strong> {(scanResult.confidence_score * 100).toFixed(0)}%</Typography>
              {scanResult.reasons && scanResult.reasons.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Reasons:</Typography>
                  {scanResult.reasons.map((reason: any, idx: number) => (
                    <Chip
                      key={idx}
                      label={reason.description}
                      size="small"
                      color={reason.type === 'missing_information' ? 'default' : 'warning'}
                      sx={{ mr: 1, mt: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Alert>
          )}

          {!searchLoading && !scanResult && searchResults.length > 0 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((candidate: any) => (
                    <TableRow key={candidate.id}>
                      <TableCell>
                        <Typography fontWeight="medium">
                          {candidate.first_name} {candidate.last_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{candidate.email || 'N/A'}</TableCell>
                      <TableCell>
                        {formatInTimeZone(new Date(candidate.created_at), 'America/New_York', 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleScanCandidate(candidate.id)}
                          disabled={searchLoading}
                        >
                          Scan
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!searchLoading && !scanResult && searchResults.length === 0 && searchQuery && (
            <Alert severity="info">
              No candidates found matching "{searchQuery}". Try a different name or email.
            </Alert>
          )}
        </DialogContent>
      </Dialog>

      {/* Floating Chat Button */}
      <Fab
        color="primary"
        aria-label="chat"
        onClick={() => setChatOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <ChatOutlined />
      </Fab>

      {/* Chat Sidebar */}
      <Drawer
        anchor="right"
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 500 },
            maxWidth: '100%',
          },
        }}
      >
        <ChatAgent onClose={() => setChatOpen(false)} />
      </Drawer>
    </Box>
  )
}
